<template>
  <v-container>
    <v-card class="mt-4 me-lg-5">
      <v-card-item>
        <v-card-title><strong>A propos</strong></v-card-title>

        <v-card-subtitle>Le projet</v-card-subtitle>
      </v-card-item>
      <v-card-text class="justify">
        <p>
          ChatSO est un agent conversationnel spécialisé pour répondre aux
          questions relatives aux données de la recherche et à la Science Ouverte.
        </p>
        <p>
          Outre sa capacité à fournir des réponses en langage naturel, ChatSO
          s'engage à présenter les sources documentaires qui ont contribué à la
          formulation de sa réponse.
        </p>
        <p>
          L'objectif fondamental de ChatSO réside dans sa mission
          d'accompagnement de la communauté scientifique en ce qui concerne la
          gestion des données et la Science Ouverte.
        </p>
        <p>
          Le projet ChatSO est né au sein du groupe de travail "Outils Applicatifs Communs" 
          des ateliers de la donnée, dans le cadre de l'écosystème Recherche Data Gouv. 
          Il vise à développer et tester la pertinence de la mise en place d'un chatbot 
          spécialisé en Science Ouverte, destiné aux personnes qui accompagnent les 
          chercheurs dans la gestion de leurs données.
        </p>
        <p>
          Ce projet open source vise à fournir un chatbot simple, léger et adaptable, 
          capable de répondre aux questions spécifiques d'un domaine tout en citant ses sources.
        </p>
        <p>
          Notre équipe pluridisciplinaire, composée de membres issus de diverses universités françaises, 
          accueille toute personne de l'Enseignement Supérieur et de la Recherche (ESR) souhaitant contribuer !
        </p>
      </v-card-text>

      <v-card-subtitle>Objectifs</v-card-subtitle>
      <v-card-text class="justify">
        <ul>
          <li>Développer un chatbot spécialisé en Science Ouverte</li>
          <li>Offrir une solution simple à installer et peu gourmande en ressources</li>
          <li>Garantir la transparence et la fiabilité des informations via la citation des sources</li>
          <li>Créer un outil adaptable à d'autres domaines que la Science Ouverte</li>
        </ul>
      </v-card-text>

      <v-card-subtitle>Nouvelles fonctionnalités</v-card-subtitle>
      <v-card-text class="justify">
        <ul>
          <li>
            <p>
              <strong>Support des fichiers Markdown</strong> : En complément des PDF, ChatSO intègre désormais 
              des contenus extraits de sites web au format Markdown. Cela permet d'inclure des ressources 
              comme la liste des <strong>entrepôts de confiance</strong> de recherche.data.gouv.fr et le 
              site juridique <strong>JurisDoR</strong> (https://jurisdor.fr/) spécialisé dans les aspects 
              juridiques de la science ouverte.
            </p>
          </li>
          <li>
            <p>
              <strong>Système d'évaluation des réponses</strong> : Vous pouvez désormais évaluer la qualité 
              des réponses fournies par ChatSO en attribuant une note de 1 à 5 étoiles. 
              <strong>Important</strong> : En évaluant une réponse, vous acceptez que votre question soit 
              utilisée à des fins d'amélioration du chatbot. Ces données nous permettent d'identifier les 
              points faibles du système et d'améliorer continuellement la qualité des réponses. Votre adresse 
              IP est anonymisée (dernier octet supprimé) avant stockage.
            </p>
          </li>
          <li>
            <p>
              <strong>Liens cliquables</strong> : Toutes les URLs présentes dans les réponses sont désormais 
              cliquables et s'ouvrent dans un nouvel onglet pour faciliter la consultation des sources.
            </p>
          </li>
        </ul>
      </v-card-text>

      <v-card-subtitle>Technologies utilisées</v-card-subtitle>
      <v-card-text class="justify">
        <ul>
          <li>
            <p>
              <strong>OpenAI-compatible API</strong> : Interface standardisée permettant l'utilisation 
              de différents modèles de langage (OpenAI, modèles locaux, gateway universitaire) - 
              Actuellement connecté au gateway de l'Université de Strasbourg (gateway-ia.app.unistra.fr)
            </p>
          </li>
          <li>
            <p>
              <strong>Modèles LLM supportés</strong> : gpt-oss, qwen3 (par défaut), lucie, mistral-small - 
              Possibilité de comparer les réponses de plusieurs modèles simultanément
            </p>
          </li>
          <li>
            <p>
              <strong>HuggingFaceEmbedding</strong> : Outil qui permet de transformer des mots ou 
              des phrases en représentations numériques (vecteurs) afin de mieux comprendre leur 
              signification et de les utiliser dans des applications de traitement du langage naturel, 
              comme la recherche ou la classification. Modèle utilisé : sentence-transformers/all-MiniLM-L6-v2
            </p>
          </li>
          <li>
            <p>
              <strong>FastAPI</strong> : Framework utilisé pour la création d'APIs en Python.
            </p>
          </li>
          <li>
            <p>
              <strong>ChromaDB</strong> : Base de données vectorielle pour le stockage et la 
              récupération d'informations (RAG) - 11,656 chunks de documents issus de 200 PDFs
            </p>
          </li>
          <li>
            <p>
              <strong>VueJS 3 et Vuetify 3</strong> : Framework JavaScript utilisé pour le 
              développement de l'interface web
            </p>
          </li>
          <li>
            <p>
              <strong>Nginx</strong> : Serveur web et reverse proxy pour servir l'application 
              et sécuriser les connexions (HTTPS avec Let's Encrypt)
            </p>
          </li>
          <li>
            <p>
              <strong>Base de connaissances</strong> : Alimentée par 200 PDF sélectionnés sur 
              la Science Ouverte en France, traités avec OCR français (Tesseract)
            </p>
          </li>
        </ul>
      </v-card-text>

      <v-card-subtitle>État d'avancement (Novembre 2025)</v-card-subtitle>
      <v-card-text class="justify">
        <ul>
          <li>Version déployée en production sur https://chatso-test.app.unistra.fr/</li>
          <li>Backend API utilisant le gateway OpenAI-compatible de l'Université de Strasbourg</li>
          <li>Support de multiples modèles LLM (gpt-oss, qwen3, lucie, mistral-small)</li>
          <li>Base RAG avec 12,813 chunks issus de 206 documents (200 PDF + 6 fichiers Markdown)</li>
          <li>Intégration de ressources web : entrepôts de confiance et JurisDoR</li>
          <li>Système d'évaluation des réponses avec base de données SQLite</li>
          <li>Affichage des sources (RAG) et rendu markdown avec liens cliquables</li>
          <li>Page de test et comparaison de modèles accessible à /modeltesting</li>
          <li>Sécurisé avec HTTPS (certificat Let's Encrypt)</li>
        </ul>
      </v-card-text>

      <v-card-subtitle>Roadmap</v-card-subtitle>
      <v-card-text class="justify">
        <ul>
          <li>Intégration du contenu de pages web dans le RAG</li>
          <li>Optimisation de la recherche d'informations par détermination préalable du sujet</li>
          <li>Amélioration de l'interface utilisateur pour l'affichage des sources</li>
          <li>Support de modèles multimodaux (images, graphiques)</li>
          <li>Amélioration continue des performances et de la qualité des réponses</li>
        </ul>
      </v-card-text>

      <v-card-subtitle>Documentation / Comment contribuer</v-card-subtitle>
      <v-card-text class="justify">
        <p>
          Nous accueillons toute contribution, qu'elle soit technique, rédactionnelle ou conceptuelle.
        </p>
        <p>Pour participer :</p>
        <ul>
          <li>Consultez notre dépôt GitLab : https://gitlab.in2p3.fr/ateliersdeladonneesgt1/chatbot-recherche</li>
          <li>Contactez-nous</li>
          <li>Soumettez vos pull requests ou ouvrez des issues pour discuter de nouvelles idées</li>
        </ul>
      </v-card-text>

      <v-card-subtitle>Vos données</v-card-subtitle>
      <v-card-text class="justify">
        <p>
          <strong>Données personnelles et informations nominatives</strong>
        </p>
        <p>
          La présente information décrit les collectes et traitements de données
          personnelles des internautes utilisant l'outil « ChatSO » sous la
          responsabilité de Virgile Jarrige. Le traitement de vos données est
          nécessaire aux fins des intérêts légitimes poursuivis par le
          responsable du traitement.
        </p>
        <p><strong> Les finalités de traitement </strong></p>
        <ul>
          <li>
            <p>
              L'obtention de réponses lors de l'utilisation de l'agent
              conversationnel ChatSO
            </p>
          </li>
          <li>
            <p>L'amélioration continue du chatbot et de la qualité des réponses</p>
          </li>
          <li>
            <p>
              La gestion technique du site, sécurité du site, statistiques de
              fréquentation
            </p>
          </li>
        </ul>
        <p><strong>Données collectées</strong></p>
        <p>
          <strong>Lors de l'évaluation d'une réponse</strong> (système de notation par étoiles, facultatif) :
        </p>
        <ul>
          <li>Question posée au chatbot</li>
          <li>Réponse générée par le chatbot</li>
          <li>Note attribuée (1 à 5 étoiles)</li>
          <li>Horodatage de l'évaluation</li>
          <li>Adresse IP anonymisée (dernier octet supprimé, exemple: 192.168.1.xxx)</li>
          <li>User-Agent (type de navigateur et système d'exploitation)</li>
        </ul>
        <p>
          <strong>Données de connexion</strong> (logs serveur) :
        </p>
        <ul>
          <li>Horodatage des requêtes</li>
          <li>Adresse IP</li>
          <li>Données techniques relatives à l'équipement et au navigateur utilisés</li>
        </ul>
        <p>
          <strong>Aucune donnée d'identification personnelle</strong> (nom, prénom, email) n'est collectée. 
          L'utilisation de ChatSO est totalement anonyme.
        </p>
        <p>
          <strong>Conservation des données</strong>
        </p>
        <p>
          Les données d'évaluation (questions, réponses, notes) sont conservées 
          indéfiniment à des fins d'amélioration du service. Les données de connexion 
          (logs serveur) sont conservées conformément aux obligations légales.
        </p>
        <p>
          Les conversations sont sauvegardées localement dans votre navigateur pour une 
          durée maximale de 30 jours et ne sont jamais transmises au serveur.
        </p>
        <p><strong>Droits et accès</strong></p>
        <p>
          L'utilisation de ChatSO ne nécessite aucune inscription ni authentification. 
          Aucune donnée personnelle identifiable n'est collectée lors de l'utilisation 
          normale du chatbot.
        </p>
        <p>
          Si vous choisissez d'évaluer une réponse, vous acceptez que votre question 
          et la réponse associée soient stockées de manière anonymisée pour améliorer 
          le service.
        </p>
        <p>
          Conformément aux dispositions légales et réglementaires applicables,
          en particulier la loi n° 78-17 du 6 janvier 1978 modifiée relative à
          l'informatique, aux fichiers et aux libertés et le règlement européen
          n° 2016/679/UE du 27 avril 2016 (applicable depuis le 25 mai 2018),
          vous disposez des droits suivants :
        </p>
        <ul>
          <li>
            <p>
              Exercer votre droit d'accès, pour connaître les données
              personnelles qui vous concernent ;
            </p>
          </li>
          <li>
            <p>Demander la portabilité ou la suppression de vos données ;</p>
          </li>
          <li>
            <p>Demander la limitation du traitement de vos données ;</p>
          </li>
          <li>
            <p>
              Vous opposer, pour des motifs légitimes, au traitement de vos
              données ;
            </p>
          </li>
        </ul>
        <p>
          Vous pouvez exercer ces droits en vous adressant à :
          virgile.jarrige@unistra.fr
        </p>
        <p>
          Si vous estimez, après nous avoir contactés, que vos droits
          Informatique et Libertés ne sont pas respectés, vous avez la
          possibilité d'introduire une réclamation auprès de la CNIL.
        </p>
      </v-card-text>

      <v-card-subtitle>Cookies</v-card-subtitle>
      <v-card-text class="justify">
        <p>
          Le présent site utilise des cookies, leur gestion est élaborée en
          adéquation avec sa mission d'appui à la recherche et sont nécessaires
          aux fins des intérêts légitimes poursuivis par le responsable du
          traitement.
        </p>
      </v-card-text>

      <v-card-subtitle>Licence</v-card-subtitle>
      <v-card-text class="justify">
        <p>
          ChatSO est distribué sous licence CeCILL-B, ce qui signifie que vous êtes libre de partager et d'adapter le logiciel.
        </p>
        <p>
          Licence CeCILL-B V1
          (https://cecill.info/licences/Licence_CeCILL-B_V1-fr.html)
        </p>
        <!--img src="../assets/cecill.png" alt="Licence Cecill BY" width="150" /-->
      </v-card-text>

      <v-card-subtitle>Contact</v-card-subtitle>
      <v-card-text class="justify">
        <p>ateliers-donnee-gt1@groupes.renater.fr</p>
      </v-card-text>

      <v-card-subtitle>Crédits</v-card-subtitle>
      <v-card-text class="justify">
        <ul>
          <li>
            <p>Max Beligne</p>
          </li>
          <li>
            <p>Arthur Brody</p>
          </li>
          <li>
            <p>Virgile Jarrige</p>
          </li>
          <li>
            <p>Elisa Michelet</p>
          </li>
          <li>
            <p>Naava Nawong-Hedwig</p>
          </li>
          <li>
            <p>Zinaida Nistrean</p>
          </li>
          <li>
            <p>Rémi Ossant</p>
          </li>
          <li>
            <p>Clément Plancq</p>
          </li>
          <li>
            <p>François Quéré</p>
          </li>
          <li>
            <p>Nadir Tabbou</p>
          </li>
          <li>
            <p>Alexandrina Turcanu</p>
          </li>
        </ul>
      </v-card-text>

      <v-card-subtitle>Pour en savoir plus</v-card-subtitle>
      <v-card-text class="justify">
        <p>
          Pour en savoir plus sur la Science Ouverte et son importance dans la recherche moderne, 
          visitez https://www.ouvrirlascience.fr.
        </p>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "AboutChatRDG",
  props: [],
  components: {},
  data: () => ({}),
};
</script>

<style></style>
